import { useState, useEffect, useCallback, useMemo } from "react";
import api from "@/services/api";
import {
  Building2,
  ExternalLink,
  FileText,
  Euro,
  Calendar,
  Tag,
  Target,
  User,
  MessageSquare,
  ArrowRight,
  X,
  CheckCircle2,
  Clock,
  AlertCircle,
  XCircle,
  Search,
  Globe,
  Briefcase,
  Hash,
  Radar,
  Phone,
  Mail,
  MapPin,
  Users,
  FileDown,
  Loader2,
  RefreshCw,
  Award,
  TrendingUp,
  History,
  Plus,
  Copy,
  Link,
  Info,
  Sparkles,
  Zap,
  UserPlus,
  Trophy,
  CheckCheck,
  Linkedin
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import ConvertirLeadModal from "@/components/ConvertirLeadModal";
import DescartarModal from "@/components/DescartarModal";
import AdjudicatarioHistorialModal from "@/components/AdjudicatarioHistorialModal";
import ErrorBoundary from "@/components/ErrorBoundary";

// Score level config
const getScoreLevel = (score) => {
  if (score >= 85) return { level: "critico", color: "bg-red-500", textColor: "text-red-400", label: "Crítico" };
  if (score >= 70) return { level: "alto", color: "bg-orange-500", textColor: "text-orange-400", label: "Alto" };
  if (score >= 50) return { level: "medio", color: "bg-yellow-500", textColor: "text-yellow-400", label: "Medio" };
  return { level: "bajo", color: "bg-slate-500", textColor: "text-slate-400", label: "Bajo" };
};

// Estado config
const estadoConfig = {
  nueva: { icon: AlertCircle, color: "text-cyan-400", bg: "bg-cyan-500/20", label: "Nuevo" },
  revisada: { icon: Clock, color: "text-yellow-400", bg: "bg-yellow-500/20", label: "En revisión" },
  contactada: { icon: Building2, color: "text-purple-400", bg: "bg-purple-500/20", label: "Contactada" },
  convertida: { icon: CheckCircle2, color: "text-green-400", bg: "bg-green-500/20", label: "Convertida" },
  descartada: { icon: XCircle, color: "text-slate-400", bg: "bg-slate-500/20", label: "Descartada" },
};

// Estado PLACSP
const estadoPlacspLabels = {
  "PUB": "Publicada",
  "ADJ": "Adjudicada",
  "RES": "Resuelta",
  "EV": "En evaluación",
  "AN": "Anulada"
};

// Tipo contrato
const tipoContratoLabels = {
  "1": "Obras",
  "2": "Servicios",
  "3": "Suministros",
  "21": "Gestión de servicios públicos",
  "31": "Concesión de obras"
};

export default function OportunidadModal({ open, onClose, oportunidad, user }) {
  const [loading, setLoading] = useState(false);
  const [estado, setEstado] = useState("");
  const [notas, setNotas] = useState("");
  const [asignadoId, setAsignadoId] = useState("");
  const [users, setUsers] = useState([]);
  const [convertirModalOpen, setConvertirModalOpen] = useState(false);
  const [descartarModalOpen, setDescartarModalOpen] = useState(false);
  const [historialAdjOpen, setHistorialAdjOpen] = useState(false);
  const [postmortem, setPostmortem] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  // Activities state
  const [activities, setActivities] = useState([]);
  const [newActivity, setNewActivity] = useState("");
  const [activityType, setActivityType] = useState("nota");

  // Documents/Acta state from fetcher
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [acta, setActa] = useState(null);
  const [pliego, setPliego] = useState(null);
  const [docsData, setDocsData] = useState(null);
  const [docsLoaded, setDocsLoaded] = useState(false);

  // IA Analysis state (v3.3)
  const [painResult, setPainResult] = useState(oportunidad?.pain_analysis || null);
  const [pliegoResult, setPliegoResult] = useState(oportunidad?.pliego_analysis?.analisis || null);
  const [loadingPain, setLoadingPain] = useState(false);
  const [loadingPliego, setLoadingPliego] = useState(false);
  const [loadingEnrich, setLoadingEnrich] = useState(false);
  const [adjData, setAdjData] = useState(oportunidad?.adjudicatario_data || null);

  // v4.4.30: Competidores → Leads (Hot Leads)
  const [convertingIdx, setConvertingIdx] = useState(null);   // índice en curso
  const [convertingAll, setConvertingAll] = useState(false);
  const [convertedLeads, setConvertedLeads] = useState({});   // { idx: lead_id }

  // Quick-access document links — prefer PDF format, fallback to any
  const docLinks = useMemo(() => {
    const docs = docsData?.documents || oportunidad?.documentos_disponibles || [];
    const pptUrl = docsData?.pliego_tecnico_url || oportunidad?.pliego_tecnico_url || null;
    const findDoc = (...tipos) =>
      docs.find(d => tipos.includes(d.tipo) && (!d.formato || d.formato === "PDF"))?.url ||
      docs.find(d => tipos.includes(d.tipo))?.url ||
      null;

    return {
      ppt: pptUrl || findDoc("pliego_tecnico"),
      pliego: findDoc("pliego", "pliego_administrativo"),
      acta: findDoc("acta", "adjudicacion"),
      placsp: oportunidad?.url_placsp || null,
    };
  }, [docsData, oportunidad]);

  // Fetch users for assignment
  useEffect(() => {
    if (open) {
      fetchUsers();
    }
  }, [open]);

  // Cargar datos completos cuando el modal se abre
  useEffect(() => {
    if (oportunidad && open) {
      setEstado(oportunidad.estado || "nueva");
      setNotas(oportunidad.notas || "");
      setAsignadoId(oportunidad.asignado_id || "");
      setHasChanges(false);
      setActivities([]);
      setAdjData(oportunidad.adjudicatario_data || null);

      // Cargar datos frescos del backend (puede tener analysis de batch)
      loadFullOportunidad(oportunidad.oportunidad_id);
    }
  }, [oportunidad, open]);

  // Carga la oportunidad completa y auto-popula estados
  const loadFullOportunidad = async (opId) => {
    if (!opId) return;

    try {
      const resp = await api.get(`/oportunidades/${opId}`);
      const data = resp.data;

      // Auto-cargar Pain Analysis si ya existe
      if (data.pain_analysis) {
        setPainResult(data.pain_analysis);
      } else {
        setPainResult(null);
      }

      // Auto-cargar Pliego Analysis si ya existe
      if (data.pliego_analysis?.analisis) {
        setPliegoResult(data.pliego_analysis.analisis);
      } else {
        setPliegoResult(null);
      }

      // Auto-cargar Post-mortem si ya existe (v4.4.11)
      setPostmortem(data.postmortem_analysis || null);

      // Auto-cargar documentos si ya existen
      if (data.documentos_disponibles && data.documentos_disponibles.length > 0) {
        setDocsData({ documents: data.documentos_disponibles, pliego_tecnico_url: data.pliego_tecnico_url });
        setPliego({
          disponible: true,
          documentos: data.documentos_disponibles,
          pliego_tecnico_url: data.pliego_tecnico_url,
          total_documentos: data.documentos_disponibles.length,
        });
        setDocsLoaded(true);
      } else {
        setDocsData(null);
        setPliego(null);
        setDocsLoaded(false);
      }

      // Auto-cargar adjudicatario_data si existe
      if (data.adjudicatario_data) {
        setAdjData(data.adjudicatario_data);
      }

      // Auto-cargar acta si ya existe.
      // v4.4.21: normalizar — si acta_data viene como string Python repr
      // ("{'key': None, ...}"), lo convertimos a objeto para evitar crash
      // en los componentes que hacen acta.campo.
      let actaNormalizada = data.acta_data;
      if (typeof actaNormalizada === "string") {
        try {
          // Intento 1: JSON puro
          actaNormalizada = JSON.parse(actaNormalizada);
        } catch {
          try {
            // Intento 2: repr de Python → convertir a JSON válido
            const jsonable = actaNormalizada
              .replace(/'/g, '"')
              .replace(/\bNone\b/g, "null")
              .replace(/\bTrue\b/g, "true")
              .replace(/\bFalse\b/g, "false");
            actaNormalizada = JSON.parse(jsonable);
          } catch {
            // No se puede parsear → tratamos como ausente
            actaNormalizada = null;
          }
        }
      }
      if (actaNormalizada && typeof actaNormalizada === "object" && !Array.isArray(actaNormalizada)) {
        setActa(actaNormalizada);
      } else {
        setActa(null);
      }

      // AUTO-ANALIZAR: si no hay análisis de pliego ni documentos, lanzar automáticamente
      if (!data.pliego_analysis && !data.documentos_disponibles && data.ref_placsp) {
        toast.info("Descargando y analizando documentos...");
        // Pequeño delay para que el modal se renderice primero
        setTimeout(() => {
          analizarPliego();
        }, 500);
      }

    } catch (err) {
      console.error("Error loading full oportunidad:", err);
      toast.error("Error cargando datos de la oportunidad");
      // Fallback: usar datos del prop
      setPainResult(oportunidad?.pain_analysis || null);
      setPliegoResult(oportunidad?.pliego_analysis?.analisis || null);
      setActa(null);
      setPliego(null);
      setDocsData(null);
      setDocsLoaded(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data);
    } catch (error) {
      console.error("Error fetching users:", error);
      toast.error("Error cargando usuarios");
    }
  };

  // Analizar pliego - llama al fetcher para descargar y parsear documentos
  const analizarPliego = async () => {
    if (!oportunidad?.ref_placsp) return;

    setLoadingDocs(true);
    try {
      toast.info("Solicitando documentos al fetcher...");

      // Primero intentamos descargar (fetch-batch)
      await api.post(
        `/spotter/documents/fetch/${encodeURIComponent(oportunidad.ref_placsp)}`,
        {}
      );

      // Luego obtenemos los datos
      const response = await api.get(
        `/spotter/documents/${encodeURIComponent(oportunidad.ref_placsp)}`
      );

      const data = response.data;
      setDocsData(data);

      if (data.acta) {
        setActa(data.acta);
      }
      if (data.pliego) {
        setPliego(data.pliego);
      }

      setDocsLoaded(true);

      if (data.acta?.disponible) {
        toast.success("Acta de adjudicación cargada");
      } else if (data.status === "parcial") {
        toast.info("Datos parciales - algunos documentos no disponibles");
      } else {
        toast.info("Documentos procesados");
      }

    } catch (error) {
      console.error("Error analyzing pliego:", error);
      toast.error("Error al analizar documentos");
    } finally {
      setLoadingDocs(false);
    }

    // Lanzar Pain Analysis en paralelo (no bloquea)
    runPainAnalysis();
    // Lanzar Pliego Analysis en paralelo
    runPliegoAnalysis();
  };

  // Pain Analysis - IA con fallback (Gemini > OpenAI > Claude)
  const runPainAnalysis = async () => {
    if (!oportunidad?.oportunidad_id) return;
    setLoadingPain(true);
    try {
      const resp = await api.post(
        `/analyzer/pain/${oportunidad.oportunidad_id}`,
        {}
      );
      setPainResult(resp.data);
      toast.success(`Pain Score: ${resp.data.pain_score} (${resp.data.pain_level}) vía ${resp.data.llm_provider || 'reglas'}`);
    } catch (error) {
      console.error("Pain analysis error:", error);
      toast.error("Error en Pain Analysis");
    } finally {
      setLoadingPain(false);
    }
  };

  // Pliego Analysis - IA con fallback (v3.4: ahora descarga y lee el PDF real)
  const runPliegoAnalysis = async () => {
    if (!oportunidad?.oportunidad_id) return;
    setLoadingPliego(true);
    try {
      const resp = await api.post(
        `/analyzer/pliego/${oportunidad.oportunidad_id}`,
        {}
      );
      if (resp.data.analisis) {
        setPliegoResult(resp.data.analisis);
        const docsCount = resp.data.docs_analizados || 0;
        toast.success(`Pliego analizado vía ${resp.data.llm_provider || 'IA'} (${docsCount} docs leídos)`);
      }
      // Actualizar documentos si el analyzer los devuelve
      if (resp.data.documentos_disponibles && resp.data.documentos_disponibles.length > 0) {
        setDocsData(prev => ({
          ...prev,
          documents: resp.data.documentos_disponibles,
          pliego_tecnico_url: resp.data.pliego_tecnico_url,
        }));
      }
    } catch (error) {
      console.error("Pliego analysis error:", error);
      toast.error("Error en Pliego Analysis");
    } finally {
      setLoadingPliego(false);
    }
  };

  // Enriquecer datos del adjudicatario con IA
  const enrichAdjudicatario = async () => {
    if (!oportunidad?.oportunidad_id) return;
    setLoadingEnrich(true);
    try {
      const resp = await api.post(
        `/analyzer/enrich/${oportunidad.oportunidad_id}`,
        {}
      );
      const newAdjData = resp.data.adjudicatario_data;
      setAdjData(newAdjData);
      const fields = resp.data.fields_added || [];
      const confianza = resp.data.confianza || "?";
      toast.success(
        `Datos enriquecidos (${confianza}) vía ${resp.data.provider || "IA"}` +
        (fields.length > 0 ? `: ${fields.join(", ")}` : "")
      );
    } catch (error) {
      const msg = error.response?.data?.detail || "Error al enriquecer";
      toast.error(msg);
      console.error("Enrich error:", error);
    } finally {
      setLoadingEnrich(false);
    }
  };

  // v4.4.30: Competidor → Lead (1 solo)
  const convertCompetidorToLead = async (idx) => {
    if (!oportunidad?.oportunidad_id) return;
    setConvertingIdx(idx);
    try {
      const resp = await api.post(
        `/oportunidades/${oportunidad.oportunidad_id}/competidor-to-lead`,
        { competidor_index: idx }
      );
      setConvertedLeads((prev) => ({ ...prev, [idx]: resp.data.lead_id }));
      if (resp.data.ya_existia) {
        toast.info(`Lead ya existía: ${resp.data.empresa}`);
      } else {
        toast.success(`Lead creado: ${resp.data.empresa}`);
      }
    } catch (error) {
      const msg = error.response?.data?.detail || "Error al crear lead";
      toast.error(msg);
      console.error("Competidor-to-lead error:", error);
    } finally {
      setConvertingIdx(null);
    }
  };

  // v4.4.30: Competidores → Leads (batch, todos a la vez)
  const convertAllCompetidoresToLeads = async () => {
    if (!oportunidad?.oportunidad_id) return;
    setConvertingAll(true);
    try {
      const resp = await api.post(
        `/oportunidades/${oportunidad.oportunidad_id}/competidores-to-leads-batch`,
        {}
      );
      const { leads_creados, ya_existian, sin_nombre_ignorados, resultados } = resp.data;
      // Mapear resultados al state
      const newMap = {};
      (resultados || []).forEach((r, i) => {
        if (r.lead_id) newMap[i] = r.lead_id;
      });
      setConvertedLeads((prev) => ({ ...prev, ...newMap }));
      toast.success(
        `${leads_creados} leads creados` +
          (ya_existian ? `, ${ya_existian} ya existían` : "") +
          (sin_nombre_ignorados ? `, ${sin_nombre_ignorados} ignorados` : "")
      );
    } catch (error) {
      const msg = error.response?.data?.detail || "Error en batch";
      toast.error(msg);
      console.error("Batch error:", error);
    } finally {
      setConvertingAll(false);
    }
  };

  const fetchActivities = useCallback(async () => {
    if (!oportunidad?.oportunidad_id) return;

    try {
      const response = await api.get(
        `/oportunidades/${oportunidad.oportunidad_id}/actividades`
      );
      setActivities(response.data);
    } catch (error) {
      console.error("Error fetching activities:", error);
      toast.error("Error cargando actividades");
    }
  }, [oportunidad]);

  const handleSave = async () => {
    if (!oportunidad) return;
    setLoading(true);

    try {
      await api.put(
        `/oportunidades/${oportunidad.oportunidad_id}`,
        { estado, notas, asignado_id: asignadoId || null }
      );
      toast.success("Oportunidad actualizada");
      setHasChanges(false);
      onClose();
    } catch (error) {
      toast.error("Error al actualizar");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDescartar = () => {
    setDescartarModalOpen(true);
  };

  const handleAddActivity = async () => {
    if (!newActivity.trim()) return;

    try {
      await api.post(
        `/oportunidades/${oportunidad.oportunidad_id}/actividades`,
        { tipo: activityType, descripcion: newActivity }
      );
      toast.success("Actividad añadida");
      setNewActivity("");
      fetchActivities();
    } catch (error) {
      toast.error("Error al añadir actividad");
    }
  };

  const handleFieldChange = (field, value) => {
    setHasChanges(true);
    if (field === "estado") setEstado(value);
    if (field === "notas") setNotas(value);
    if (field === "asignadoId") setAsignadoId(value);
  };

  const copyToClipboard = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copiado`);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      toast.success(`${label} copiado`);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("es-ES", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 0,
    }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleString("es-ES", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (!oportunidad) return null;

  const scoreLevel = getScoreLevel(oportunidad.score);
  const estadoInfo = estadoConfig[oportunidad.estado] || estadoConfig.nueva;
  const EstadoIcon = estadoInfo.icon;

  // Check if we have adjudicatario data
  const hasAdjudicatario = oportunidad.adjudicatario &&
    oportunidad.adjudicatario !== "Sin adjudicatario" &&
    oportunidad.adjudicatario !== "Pendiente";

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="bg-slate-900 border-slate-700 w-[95vw] max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <DialogHeader className="pb-4 border-b border-slate-800 pr-10">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-start gap-2">
                  <DialogTitle className="text-lg font-bold text-white leading-snug tracking-normal line-clamp-2 flex-1">
                    {oportunidad.titulo || "Sin título"}
                  </DialogTitle>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="shrink-0 h-8 w-8 text-slate-400 hover:text-white hover:bg-slate-700"
                    title="Copiar enlace"
                    onClick={() => {
                      const id = oportunidad.oportunidad_id || oportunidad._id || oportunidad.id;
                      const url = `${window.location.origin}/oportunidades?open=${id}`;
                      const importe = oportunidad.importe_adjudicacion
                        ? new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", minimumFractionDigits: 0 }).format(oportunidad.importe_adjudicacion)
                        : "";
                      const lines = [`📋 OPP | ${(oportunidad.titulo || "").substring(0, 80)}`];
                      if (importe) lines.push(`💰 ${importe}`);
                      if (oportunidad.adjudicatario_nombre) lines.push(`🏢 ${oportunidad.adjudicatario_nombre}`);
                      lines.push(`🔗 ${url}`);
                      navigator.clipboard.writeText(lines.join("\n"));
                      toast.success("Enlace copiado");
                    }}
                  >
                    <Link className="w-4 h-4" />
                  </Button>
                </div>
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  {/* Score Badge */}
                  <Badge className={`${scoreLevel.color} text-white font-bold font-mono`}>
                    {oportunidad.score} pts
                  </Badge>

                  {/* Estado interno */}
                  <Badge className={`${estadoInfo.bg} ${estadoInfo.color} border-0`}>
                    <EstadoIcon className="w-3 h-3 mr-1" />
                    {estadoInfo.label}
                  </Badge>

                  {/* Estado PLACSP */}
                  <Badge variant="outline" className="border-slate-600 text-slate-300">
                    {estadoPlacspLabels[oportunidad.estado_placsp] || oportunidad.estado_placsp || "?"}
                  </Badge>

                  {/* Tipo contrato */}
                  {oportunidad.tipo_contrato_code && (
                    <Badge variant="outline" className="border-purple-500/50 text-purple-400">
                      {tipoContratoLabels[oportunidad.tipo_contrato_code] || `Tipo ${oportunidad.tipo_contrato_code}`}
                    </Badge>
                  )}

                  {/* Fondos europeos */}
                  {oportunidad.fondos_ue && (
                    <Badge className="bg-yellow-500/20 text-yellow-400 border border-yellow-500/30">
                      <Euro className="w-3 h-3 mr-1" />
                      Fondos UE
                    </Badge>
                  )}
                </div>
              </div>
            </div>
          </DialogHeader>

          {/* Quick-access document links bar — solo muestra botones con URL real */}
          <div className="flex items-center gap-2 py-2 px-1 border-b border-slate-800 flex-wrap">
            {[
              { key: "ppt", label: "PPT", url: docLinks.ppt, color: "text-green-400 hover:bg-green-500/15 border-green-500/30" },
              { key: "pliego", label: "Pliego", url: docLinks.pliego, color: "text-blue-400 hover:bg-blue-500/15 border-blue-500/30" },
              { key: "acta", label: "Acta", url: docLinks.acta, color: "text-yellow-400 hover:bg-yellow-500/15 border-yellow-500/30" },
              { key: "placsp", label: "PLACSP", url: docLinks.placsp, color: "text-cyan-400 hover:bg-cyan-500/15 border-cyan-500/30" },
            ].filter(({ key, url }) => key === "placsp" || url)
             .map(({ key, label, url, color }) => (
              <Button
                key={key}
                variant="outline"
                size="sm"
                className={`h-7 text-xs border ${color}`}
                onClick={() => window.open(url, "_blank")}
                title={`Abrir ${label}`}
              >
                {key === "ppt" && <FileDown className="w-3 h-3 mr-1" />}
                {key === "pliego" && <FileText className="w-3 h-3 mr-1" />}
                {key === "acta" && <Award className="w-3 h-3 mr-1" />}
                {key === "placsp" && <Globe className="w-3 h-3 mr-1" />}
                {label}
                <ExternalLink className="w-3 h-3 ml-1" />
              </Button>
            ))}
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto py-4">
            <Tabs defaultValue="expediente" className="w-full">
              <TabsList className="bg-slate-800/50 mb-4 grid grid-cols-4 w-full">
                <TabsTrigger value="expediente" className="data-[state=active]:bg-cyan-500/20">
                  <FileText className="w-4 h-4 mr-2" />
                  Expediente
                </TabsTrigger>
                <TabsTrigger value="empresa" className="data-[state=active]:bg-cyan-500/20">
                  <Building2 className="w-4 h-4 mr-2" />
                  Empresas
                </TabsTrigger>
                <TabsTrigger value="analisis" className="data-[state=active]:bg-cyan-500/20">
                  <TrendingUp className="w-4 h-4 mr-2" />
                  Análisis
                </TabsTrigger>
                <TabsTrigger value="gestion" className="data-[state=active]:bg-cyan-500/20" onClick={fetchActivities}>
                  <Briefcase className="w-4 h-4 mr-2" />
                  Gestión
                </TabsTrigger>
              </TabsList>

              {/* ===================== TAB: EXPEDIENTE ===================== */}
              <TabsContent value="expediente" className="space-y-4">
                {/* Identificación */}
                <div className="p-4 bg-slate-800/50 rounded-lg space-y-3">
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-1">
                      <Label className="text-slate-500 text-xs">Nº Expediente</Label>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-white font-bold">{oportunidad.expediente_id || "-"}</span>
                        {oportunidad.expediente_id && (
                          <Button variant="ghost" size="icon" className="h-6 w-6 text-slate-400 hover:text-white"
                            onClick={() => copyToClipboard(oportunidad.expediente_id, "Expediente")}
                          >
                            <Copy className="w-3 h-3" />
                          </Button>
                        )}
                      </div>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-slate-500 text-xs">Ref PLACSP</Label>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-slate-400 text-sm">{oportunidad.ref_placsp}</span>
                        <Button variant="ghost" size="icon" className="h-6 w-6 text-slate-400 hover:text-white"
                          onClick={() => copyToClipboard(oportunidad.ref_placsp, "Ref")}
                        >
                          <Copy className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-slate-500 text-xs">ID Interno</Label>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-cyan-400 text-xs">{oportunidad.oportunidad_id}</span>
                        <Button variant="ghost" size="icon" className="h-6 w-6 text-slate-400 hover:text-white"
                          onClick={() => copyToClipboard(oportunidad.oportunidad_id, "ID")}
                        >
                          <Copy className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                  {oportunidad.url_placsp && (
                    <a href={oportunidad.url_placsp} target="_blank" rel="noopener noreferrer"
                      className="text-cyan-400 hover:underline text-xs flex items-center gap-1"
                    >
                      <ExternalLink className="w-3 h-3" /> Ver expediente completo en PLACSP
                    </a>
                  )}
                </div>

                {/* Quién contrata + Quién ganó */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-slate-800/30 rounded-lg space-y-1">
                    <Label className="text-slate-500 text-xs">Órgano Contratante</Label>
                    <p className="text-white text-sm font-medium">{oportunidad.organo_contratante || "No especificado"}</p>
                    {oportunidad.nif_organo && (
                      <p className="text-slate-500 font-mono text-xs">{oportunidad.nif_organo}</p>
                    )}
                  </div>
                  <div className="p-3 bg-slate-800/30 rounded-lg space-y-1">
                    <div className="flex items-start justify-between gap-2">
                      <Label className="text-slate-500 text-xs">Adjudicatario</Label>
                      {hasAdjudicatario && (
                        <button
                          type="button"
                          onClick={() => setHistorialAdjOpen(true)}
                          className="text-[10px] text-teal-400 hover:text-teal-300 font-medium flex items-center gap-1 transition-colors"
                          title="Ver todos los contratos de esta empresa"
                        >
                          <TrendingUp className="w-3 h-3" />
                          Ver histórico
                        </button>
                      )}
                    </div>
                    <p className={`text-sm font-medium ${hasAdjudicatario ? "text-green-400" : "text-slate-400"}`}>
                      {hasAdjudicatario ? oportunidad.adjudicatario : "Pendiente"}
                    </p>
                    {oportunidad.nif_adjudicatario && hasAdjudicatario && (
                      <p className="text-slate-500 font-mono text-xs">{oportunidad.nif_adjudicatario}</p>
                    )}
                  </div>
                </div>

                {/* Datos clave */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label className="text-slate-500 text-xs">Importe estimado</Label>
                    <p className="text-xl font-bold text-green-400">{formatCurrency(oportunidad.importe)}</p>
                    {oportunidad.importe_confianza && (
                      <span className="text-xs text-slate-500">Confianza: {oportunidad.importe_confianza}</span>
                    )}
                  </div>

                  <div className="space-y-1">
                    <Label className="text-slate-500 text-xs">Tipo SRS</Label>
                    <p className="text-white">{oportunidad.tipo_srs || "-"}</p>
                  </div>

                  <div className="space-y-1">
                    <Label className="text-slate-500 text-xs">CPV Principal</Label>
                    <p className="text-white font-mono">{oportunidad.cpv_principal || "-"}</p>
                    {oportunidad.cpv_descripcion && (
                      <p className="text-xs text-slate-400">{oportunidad.cpv_descripcion}</p>
                    )}
                  </div>

                  <div className="space-y-1">
                    <Label className="text-slate-500 text-xs">Localización</Label>
                    <p className="text-white flex items-center gap-1">
                      <MapPin className="w-3 h-3 text-slate-400" />
                      {oportunidad.localizacion || "-"}
                    </p>
                  </div>

                  <div className="space-y-1">
                    <Label className="text-slate-500 text-xs">Detectado</Label>
                    <p className="text-white">{formatDate(oportunidad.fecha_deteccion)}</p>
                  </div>

                  <div className="space-y-1">
                    <Label className="text-slate-500 text-xs">Fecha adjudicación</Label>
                    <p className="text-white">{formatDate(oportunidad.fecha_adjudicacion)}</p>
                  </div>
                </div>

                {/* Fondos Europeos — siempre visible */}
                <div className={`p-3 rounded-lg border ${oportunidad.fondos_ue
                  ? "bg-yellow-500/10 border-yellow-500/30"
                  : "bg-slate-800/30 border-slate-700"
                }`}>
                  <div className="flex items-center gap-2">
                    <Euro className={`w-5 h-5 ${oportunidad.fondos_ue ? "text-yellow-400" : "text-slate-500"}`} />
                    <div>
                      <Label className={`text-xs font-medium uppercase ${oportunidad.fondos_ue ? "text-yellow-400" : "text-slate-500"}`}>
                        Fondos Europeos
                      </Label>
                      {oportunidad.fondos_ue ? (
                        <>
                          <p className="text-white font-medium">{oportunidad.fondos_ue}</p>
                          {oportunidad.fondos_ue_code && (
                            <p className="text-yellow-400/70 text-xs font-mono mt-0.5">
                              Código: {oportunidad.fondos_ue_code}
                            </p>
                          )}
                        </>
                      ) : (
                        <p className="text-slate-500 text-sm">No vinculada a fondos europeos</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Sectores */}
                {oportunidad.sectores_probable && oportunidad.sectores_probable.length > 0 && (
                  <div className="space-y-2">
                    <Label className="text-slate-500 text-xs">Sectores detectados</Label>
                    <div className="flex flex-wrap gap-2">
                      {oportunidad.sectores_probable.map((sector, idx) => (
                        <Badge key={idx} variant="outline" className="border-cyan-500/30 text-cyan-400">
                          {sector}
                          {oportunidad.sector_scores?.[sector] && (
                            <span className="ml-1 text-slate-500">({oportunidad.sector_scores[sector]})</span>
                          )}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Keywords */}
                {oportunidad.keywords_detectados && oportunidad.keywords_detectados.length > 0 && (
                  <div className="space-y-2">
                    <Label className="text-slate-500 text-xs">Keywords detectados</Label>
                    <div className="flex flex-wrap gap-1">
                      {oportunidad.keywords_detectados.map((kw, idx) => (
                        <Badge key={idx} variant="outline" className="border-slate-600 text-slate-300 text-xs">
                          {kw}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </TabsContent>

              {/* ===================== TAB: EMPRESA ===================== */}
              <TabsContent value="empresa" className="space-y-4">
                {/* Órgano contratante - SIEMPRE visible */}
                <div className="p-4 bg-slate-800/50 rounded-lg space-y-3">
                  <div className="flex items-center gap-2 text-slate-400">
                    <Briefcase className="w-4 h-4" />
                    <span className="text-xs font-medium uppercase">Órgano Contratante</span>
                  </div>
                  <p className="text-white font-medium">
                    {oportunidad.organo_contratante || "No especificado"}
                  </p>
                  {oportunidad.nif_organo && (
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-slate-400 text-sm">{oportunidad.nif_organo}</span>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 text-slate-400"
                        onClick={() => copyToClipboard(oportunidad.nif_organo, "NIF")}
                      >
                        <Copy className="w-3 h-3" />
                      </Button>
                    </div>
                  )}
                </div>

                {/* Adjudicatario - si existe */}
                {hasAdjudicatario ? (
                  <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg space-y-3">
                    <div className="flex items-center gap-2 text-green-400">
                      <Award className="w-4 h-4" />
                      <span className="text-xs font-medium uppercase">Adjudicatario (Ganador)</span>
                    </div>
                    <p className="text-white font-medium text-lg">
                      {oportunidad.adjudicatario}
                    </p>
                    {oportunidad.nif_adjudicatario && (
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-slate-400">{oportunidad.nif_adjudicatario}</span>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 text-slate-400"
                          onClick={() => copyToClipboard(oportunidad.nif_adjudicatario, "NIF")}
                        >
                          <Copy className="w-3 h-3" />
                        </Button>
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex flex-wrap gap-2 pt-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-cyan-500/50 text-cyan-300 hover:bg-cyan-500/10"
                        onClick={enrichAdjudicatario}
                        disabled={loadingEnrich}
                      >
                        {loadingEnrich ? (
                          <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Buscando...</>
                        ) : (
                          <><Sparkles className="w-4 h-4 mr-2" />Enriquecer con IA</>
                        )}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-slate-600 text-slate-300"
                        onClick={() => {
                          const url = adjData?.web || `https://www.google.com/search?q=${encodeURIComponent(oportunidad.adjudicatario)}`;
                          window.open(url, "_blank");
                        }}
                      >
                        <Globe className="w-4 h-4 mr-2" />
                        {adjData?.web ? "Web" : "Google"}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-slate-600 text-slate-300"
                        onClick={() => {
                          const url = adjData?.linkedin || `https://www.google.com/search?q=${encodeURIComponent(oportunidad.adjudicatario + " linkedin")}`;
                          window.open(url, "_blank");
                        }}
                      >
                        <Search className="w-4 h-4 mr-2" />
                        LinkedIn
                      </Button>
                    </div>

                    {/* Contacto del adjudicatario - desde adjData (local state) o acta */}
                    {(adjData?.persona_contacto || adjData?.email || adjData?.telefono ||
                      adjData?.direccion || acta?.contacto?.email || acta?.contacto?.telefono) ? (
                      <div className="mt-3 pt-3 border-t border-green-500/20 grid grid-cols-1 gap-2">
                        {(adjData?.persona_contacto) && (
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4 text-green-400/70" />
                            <span className="text-white text-sm font-medium">{adjData.persona_contacto}</span>
                            {adjData?.cargo_contacto && (
                              <span className="text-slate-400 text-xs">— {adjData.cargo_contacto}</span>
                            )}
                          </div>
                        )}
                        {(adjData?.email || acta?.contacto?.email) && (
                          <div className="flex items-center gap-2">
                            <Mail className="w-4 h-4 text-green-400/70" />
                            <a href={`mailto:${adjData?.email || acta?.contacto?.email}`}
                              className="text-cyan-400 hover:underline text-sm">
                              {adjData?.email || acta?.contacto?.email}
                            </a>
                          </div>
                        )}
                        {(adjData?.telefono || acta?.contacto?.telefono) && (
                          <div className="flex items-center gap-2">
                            <Phone className="w-4 h-4 text-green-400/70" />
                            <a href={`tel:${adjData?.telefono || acta?.contacto?.telefono}`}
                              className="text-cyan-400 hover:underline text-sm">
                              {adjData?.telefono || acta?.contacto?.telefono}
                            </a>
                          </div>
                        )}
                        {adjData?.direccion && (
                          <div className="flex items-center gap-2">
                            <MapPin className="w-4 h-4 text-green-400/70" />
                            <span className="text-slate-300 text-sm">{adjData.direccion}</span>
                          </div>
                        )}
                        {adjData?.departamento && (
                          <div className="flex items-center gap-2">
                            <Briefcase className="w-4 h-4 text-green-400/70" />
                            <span className="text-slate-300 text-sm">Depto: {adjData.departamento}</span>
                          </div>
                        )}
                        {adjData?.notas_ia && (
                          <p className="text-xs text-slate-400 mt-1 italic">{adjData.notas_ia}</p>
                        )}
                        {adjData?.ia_enrichment?.confianza && (
                          <Badge className={
                            adjData.ia_enrichment.confianza === "ALTA" ? "bg-green-500/20 text-green-400" :
                              adjData.ia_enrichment.confianza === "MEDIA" ? "bg-yellow-500/20 text-yellow-400" :
                                "bg-slate-500/20 text-slate-400"
                          }>
                            Datos IA: {adjData.ia_enrichment.confianza}
                          </Badge>
                        )}
                      </div>
                    ) : (
                      <p className="text-xs text-slate-500 mt-3 pt-3 border-t border-green-500/10">
                        Sin datos de contacto. Pulsa "Enriquecer con IA" para buscar.
                      </p>
                    )}

                    {/* PYME badge */}
                    {(adjData?.es_pyme !== undefined || acta?.es_pyme !== undefined) && (
                      <div className="mt-2">
                        <Badge className={
                          (adjData?.es_pyme || acta?.es_pyme)
                            ? "bg-green-500/20 text-green-400"
                            : "bg-slate-500/20 text-slate-400"
                        }>
                          {(adjData?.es_pyme || acta?.es_pyme) ? "PYME" : "Gran empresa"}
                        </Badge>
                      </div>
                    )}

                    {/* Hint */}
                    <p className="text-xs text-green-300/70 mt-2">
                      Esta empresa ganó el contrato y puede necesitar subcontratar servicios.
                    </p>
                  </div>
                ) : (
                  <div className="p-4 bg-slate-800/30 border border-slate-700 rounded-lg">
                    <div className="flex items-center gap-2 text-slate-500">
                      <Users className="w-4 h-4" />
                      <span className="text-xs font-medium uppercase">Adjudicatario</span>
                    </div>
                    <p className="text-slate-400 mt-2">
                      Aún no hay adjudicatario. El contrato está en estado: {estadoPlacspLabels[oportunidad.estado_placsp] || oportunidad.estado_placsp}
                    </p>
                  </div>
                )}

                {/* Perdedores - mostrar si tenemos datos del acta */}
                {acta?.otros_licitadores && acta.otros_licitadores.length > 0 ? (
                  <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg space-y-3">
                    <div className="flex items-center gap-2 text-red-400">
                      <Users className="w-4 h-4" />
                      <span className="text-xs font-medium uppercase">
                        Licitadores Perdedores ({acta.otros_licitadores.length})
                      </span>
                    </div>
                    <div className="space-y-2">
                      {acta.otros_licitadores.map((perdedor, idx) => (
                        <div key={idx} className="flex items-center justify-between p-2 bg-slate-800/50 rounded">
                          <span className="text-white">{typeof perdedor === 'string' ? perdedor : perdedor.nombre || perdedor}</span>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-slate-400 hover:text-cyan-400"
                            onClick={() => {
                              const name = typeof perdedor === 'string' ? perdedor : perdedor.nombre || perdedor;
                              window.open(`https://www.google.com/search?q=${encodeURIComponent(name)}`, "_blank");
                            }}
                          >
                            <Globe className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-red-300/70">
                      Empresas que perdieron la licitación - potenciales clientes insatisfechos.
                    </p>
                  </div>
                ) : (
                  <div className="p-4 bg-slate-800/30 border border-dashed border-slate-700 rounded-lg">
                    <div className="flex items-center gap-2 text-slate-500">
                      <Users className="w-4 h-4" />
                      <span className="text-xs font-medium uppercase">Otros Licitadores (Perdedores)</span>
                    </div>
                    <p className="text-slate-500 mt-2 text-sm">
                      Usa "Analizar Pliego" en la pestaña Análisis para obtener los perdedores.
                    </p>
                  </div>
                )}

              </TabsContent>

              {/* ===================== TAB: ANÁLISIS ===================== */}
              <TabsContent value="analisis" className="space-y-4">

                {/* === Post-Mortem IA: "¿Por qué ganó X y no Y?" (v4.4.11) === */}
                {/* v4.4.32 fix: pasar acta local para considerar docs recién descargados */}
                <ErrorBoundary label="Post-Mortem">
                  <PostMortemBlock
                    oportunidad={oportunidad}
                    acta={acta}
                    onUpdated={(pm) => setPostmortem(pm)}
                    postmortem={postmortem}
                  />
                </ErrorBoundary>

                {/* Botón Analizar Pliego */}
                <div className="p-4 bg-gradient-to-r from-cyan-500/10 to-purple-500/10 border border-cyan-500/30 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-white font-medium flex items-center gap-2">
                        <FileDown className="w-5 h-5 text-cyan-400" />
                        Análisis de Documentos
                      </h4>
                      <p className="text-slate-400 text-sm mt-1">
                        Descarga y analiza el pliego, acta de adjudicación y documentos del expediente.
                      </p>
                    </div>
                    <Button
                      className="bg-cyan-600 hover:bg-cyan-700"
                      onClick={analizarPliego}
                      disabled={loadingDocs}
                    >
                      {loadingDocs ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Radar className="w-4 h-4 mr-2" />
                      )}
                      {docsLoaded ? "Actualizar" : "Analizar Pliego"}
                    </Button>
                  </div>
                </div>

                {/* Datos del Acta si están cargados */}
                {docsLoaded && acta && (
                  <div className="p-4 bg-slate-800/50 rounded-lg space-y-3">
                    <h4 className="text-slate-400 text-xs font-medium uppercase flex items-center gap-2">
                      <Award className="w-4 h-4" />
                      Datos del Acta de Adjudicación
                    </h4>
                    <div className="grid grid-cols-2 gap-4">
                      {acta.num_licitadores && (
                        <div>
                          <p className="text-slate-500 text-xs">Nº Licitadores</p>
                          <p className="text-white font-bold text-lg">{acta.num_licitadores}</p>
                        </div>
                      )}
                      {acta.importe_adjudicacion && (
                        <div>
                          <p className="text-slate-500 text-xs">Importe Adjudicado</p>
                          <p className="text-green-400 font-bold text-lg">
                            {new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(acta.importe_adjudicacion)}
                          </p>
                        </div>
                      )}
                      {acta.es_pyme !== null && acta.es_pyme !== undefined && (
                        <div>
                          <p className="text-slate-500 text-xs">Tipo empresa</p>
                          <Badge className={acta.es_pyme ? "bg-green-500/20 text-green-400" : "bg-slate-500/20 text-slate-400"}>
                            {acta.es_pyme ? "PYME" : "Gran empresa"}
                          </Badge>
                        </div>
                      )}
                      {acta.tipo_procedimiento && (
                        <div>
                          <p className="text-slate-500 text-xs">Procedimiento</p>
                          <p className="text-white">{acta.tipo_procedimiento}</p>
                        </div>
                      )}
                    </div>
                    {acta.motivacion && (
                      <div className="mt-3 pt-3 border-t border-slate-700">
                        <p className="text-slate-500 text-xs mb-1">Motivación de adjudicación</p>
                        <p className="text-slate-300 text-sm">{acta.motivacion}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Perdedores / Hot Leads — v4.4.30 con conversión a lead */}
                {docsLoaded && acta?.otros_licitadores && acta.otros_licitadores.length > 0 ? (
                  <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg space-y-3">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div className="flex items-center gap-2 text-red-400">
                        <Users className="w-4 h-4" />
                        <span className="text-xs font-medium uppercase">
                          Competidores perdedores ({acta.otros_licitadores.length})
                        </span>
                        <Badge className="bg-red-500/20 text-red-400 ml-1">Hot Leads</Badge>
                      </div>
                      <Button
                        size="sm"
                        className="bg-red-500/20 hover:bg-red-500/30 text-red-300 border border-red-500/40"
                        onClick={convertAllCompetidoresToLeads}
                        disabled={convertingAll}
                      >
                        {convertingAll ? (
                          <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                        ) : (
                          <UserPlus className="w-3.5 h-3.5 mr-1.5" />
                        )}
                        Convertir TODOS a leads
                      </Button>
                    </div>

                    <div className="space-y-2">
                      {acta.otros_licitadores.map((perdedor, idx) => {
                        const obj = typeof perdedor === 'string'
                          ? { nombre: perdedor }
                          : (perdedor || {});
                        const nombre = obj.nombre || obj.empresa || JSON.stringify(perdedor);
                        const punt = obj.puntuacion_total ?? obj.puntuacion;
                        const puntTec = obj.puntuacion_tecnica;
                        const puntEco = obj.puntuacion_economica;
                        const oferta = obj.oferta_economica ?? obj.importe_oferta;
                        const posicion = obj.posicion;
                        const nif = obj.nif;
                        const ganadorPunt = acta.ganador_puntuacion;
                        const mejorQueGanador = (
                          typeof punt === 'number' &&
                          typeof ganadorPunt === 'number' &&
                          punt > ganadorPunt
                        );
                        const converted = convertedLeads[idx];
                        const loadingThis = convertingIdx === idx;

                        return (
                          <div key={idx} className="p-3 bg-slate-800/50 rounded border border-slate-700/50">
                            <div className="flex items-start justify-between gap-3 flex-wrap">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  {posicion && (
                                    <Badge className="bg-slate-700 text-slate-300 text-[10px]">
                                      #{posicion}
                                    </Badge>
                                  )}
                                  <span className="text-white text-sm font-medium break-words">
                                    {nombre}
                                  </span>
                                  {nif && (
                                    <span className="text-[10px] text-slate-500 font-mono">
                                      {nif}
                                    </span>
                                  )}
                                  {mejorQueGanador && (
                                    <Badge className="bg-amber-500/20 text-amber-300 border border-amber-500/40 text-[10px]">
                                      <Trophy className="w-2.5 h-2.5 mr-1" />
                                      Mejor técnica que ganador
                                    </Badge>
                                  )}
                                </div>

                                {/* Datos puntuación / oferta */}
                                {(punt != null || oferta != null || puntTec != null || puntEco != null) && (
                                  <div className="flex items-center gap-3 mt-1.5 text-xs text-slate-400 flex-wrap">
                                    {punt != null && (
                                      <span>
                                        <span className="text-slate-500">Puntos:</span>{" "}
                                        <span className="text-white font-mono font-semibold">{punt}</span>
                                      </span>
                                    )}
                                    {puntTec != null && puntTec !== punt && (
                                      <span className="text-slate-500">
                                        téc <span className="text-slate-300 font-mono">{puntTec}</span>
                                      </span>
                                    )}
                                    {puntEco != null && (
                                      <span className="text-slate-500">
                                        eco <span className="text-slate-300 font-mono">{puntEco}</span>
                                      </span>
                                    )}
                                    {oferta != null && (
                                      <span>
                                        <span className="text-slate-500">Oferta:</span>{" "}
                                        <span className="text-green-400 font-mono">
                                          {new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(oferta)}
                                        </span>
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>

                              <div className="flex items-center gap-1 flex-shrink-0">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  title="Buscar en Google"
                                  className="h-7 w-7 text-slate-400 hover:text-cyan-400"
                                  onClick={() => window.open(`https://www.google.com/search?q=${encodeURIComponent(nombre)}`, "_blank")}
                                >
                                  <Globe className="w-3.5 h-3.5" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  title="Buscar en LinkedIn"
                                  className="h-7 w-7 text-slate-400 hover:text-[#0A66C2]"
                                  onClick={() => window.open(`https://www.linkedin.com/search/results/companies/?keywords=${encodeURIComponent(nombre)}`, "_blank")}
                                >
                                  <Linkedin className="w-3.5 h-3.5" />
                                </Button>
                                {converted ? (
                                  <Button
                                    size="sm"
                                    className="h-7 bg-green-500/20 hover:bg-green-500/30 text-green-300 border border-green-500/40 text-xs"
                                    title="Lead creado"
                                    disabled
                                  >
                                    <CheckCheck className="w-3.5 h-3.5 mr-1" />
                                    Lead creado
                                  </Button>
                                ) : (
                                  <Button
                                    size="sm"
                                    className="h-7 bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-300 border border-cyan-500/40 text-xs"
                                    onClick={() => convertCompetidorToLead(idx)}
                                    disabled={loadingThis || convertingAll}
                                  >
                                    {loadingThis ? (
                                      <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                                    ) : (
                                      <UserPlus className="w-3.5 h-3.5 mr-1" />
                                    )}
                                    Convertir a Lead
                                  </Button>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <p className="text-xs text-red-300/70">
                      🔥 Estas empresas perdieron. Convierte a lead con un click y llámalas con el análisis en la mano.
                    </p>
                  </div>
                ) : docsLoaded ? (
                  /* v4.4.32 — diferenciar "no hay datos" (bug) de "no se pueden
                     publicar por tipo de procedimiento" (limitación estructural).
                     En "Negociado sin publicidad" PLACSP solo publica al
                     adjudicatario; los invitados perdedores no son públicos. */
                  (() => {
                    const tipoProc = (acta?.tipo_procedimiento || "").toLowerCase();
                    const titulo = (oportunidad?.titulo || "").toLowerCase();
                    const esContratoBasado = titulo.includes("contrato basado") || titulo.includes("acuerdo marco") || tipoProc.includes("basado en acuerdo marco");
                    const esNegociadoSinPub = tipoProc.includes("negociado sin publicidad");
                    const esRestringido = tipoProc.includes("restringido") || tipoProc.includes("diálogo competitivo") || tipoProc.includes("dialogo competitivo");
                    const esMenor = tipoProc.includes("menor");
                    const nTotal = acta?.num_licitadores || acta?.num_licitadores_total;
                    const unicoLicitador = nTotal === 1;

                    // Caso 1: Contrato basado en acuerdo marco — la competencia ya
                    // ocurrió al firmar el AM, este derivado se adjudica directo.
                    if (esContratoBasado) {
                      return (
                        <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                          <div className="flex items-center gap-2 text-amber-700 dark:text-amber-300 mb-1.5">
                            <Users className="w-4 h-4" />
                            <span className="text-xs font-semibold uppercase">Contrato basado en acuerdo marco</span>
                          </div>
                          <p className="text-amber-900/90 dark:text-amber-100/80 text-sm">
                            {unicoLicitador
                              ? "Único licitador (el adjudicatario del lote del AM). "
                              : nTotal ? `${nTotal} licitadores. ` : ""}
                            La competencia ocurrió al adjudicar el acuerdo marco original
                            — este contrato derivado se adjudica directo al proveedor
                            del lote ya seleccionado. No hay perdedores que extraer.
                          </p>
                        </div>
                      );
                    }
                    // Caso 2: Negociado sin publicidad genuino
                    if (esNegociadoSinPub) {
                      return (
                        <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                          <div className="flex items-center gap-2 text-amber-700 dark:text-amber-300 mb-1.5">
                            <Users className="w-4 h-4" />
                            <span className="text-xs font-semibold uppercase">Licitadores no divulgados</span>
                          </div>
                          <p className="text-amber-900/90 dark:text-amber-100/80 text-sm">
                            Procedimiento <span className="font-semibold">"Negociado sin publicidad"</span>
                            {unicoLicitador
                              ? " con un único licitador invitado — no hay perdedores. "
                              : nTotal ? ` con ${nTotal} licitadores invitados. ` : ". "}
                            PLACSP solo publica al adjudicatario — los invitados perdedores
                            no aparecen en documentos públicos. Limitación estructural, no un fallo del sistema.
                          </p>
                        </div>
                      );
                    }
                    if (esRestringido) {
                      return (
                        <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                          <p className="text-amber-900/90 dark:text-amber-100/80 text-sm">
                            Procedimiento <span className="font-semibold">"{acta?.tipo_procedimiento}"</span>. Los licitadores
                            perdedores pueden no haberse publicado en los documentos de adjudicación disponibles.
                          </p>
                        </div>
                      );
                    }
                    if (esMenor) {
                      return (
                        <div className="p-4 bg-slate-200/60 dark:bg-slate-800/30 border border-slate-300 dark:border-slate-700 rounded-lg">
                          <p className="text-slate-700 dark:text-slate-400 text-sm">
                            Contrato <span className="font-semibold">"{acta?.tipo_procedimiento}"</span>: sin procedimiento
                            competitivo formal, sólo se publica el adjudicatario.
                          </p>
                        </div>
                      );
                    }
                    // Procedimiento abierto u otro — aquí SÍ debería haber competidores
                    return (
                      <div className="p-4 bg-slate-200/60 dark:bg-slate-800/30 border border-slate-300 dark:border-slate-700 rounded-lg">
                        <p className="text-slate-700 dark:text-slate-400 text-sm">
                          No se extrajeron otros licitadores del acta
                          {nTotal ? ` (el acta indica ${nTotal} licitadores totales).` : "."}
                          {" "}Posibles causas: acta sin detalle competitivo,
                          PDFs incompletos, o docs pendientes de re-analizar.
                          Puedes pulsar <span className="font-semibold">"Actualizar"</span> en "Análisis de Documentos" para reintentar.
                        </p>
                      </div>
                    );
                  })()
                ) : (
                  <div className="p-4 bg-slate-800/30 border border-dashed border-slate-700 rounded-lg">
                    <div className="flex items-center gap-2 text-slate-500 mb-2">
                      <Users className="w-4 h-4" />
                      <span className="text-xs font-medium uppercase">Licitadores Perdedores</span>
                    </div>
                    <p className="text-slate-500 text-sm">
                      Pulsa "Analizar Pliego" para descargar el acta y ver los perdedores.
                    </p>
                  </div>
                )}

                {/* Score breakdown */}
                <div className="p-4 bg-slate-800/50 rounded-lg">
                  <h4 className="text-slate-400 text-xs font-medium uppercase mb-3">Score Breakdown</h4>
                  <div className="flex gap-6">
                    <div className="text-center">
                      <p className="text-2xl font-bold font-mono text-cyan-400">{oportunidad.score_breakdown?.cpv || 0}</p>
                      <p className="text-xs text-slate-500">CPV</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold font-mono text-purple-400">{oportunidad.score_breakdown?.keywords || 0}</p>
                      <p className="text-xs text-slate-500">Keywords</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold font-mono text-green-400">{oportunidad.score_breakdown?.importe || 0}</p>
                      <p className="text-xs text-slate-500">Importe</p>
                    </div>
                    <div className="text-center border-l border-slate-700 pl-6">
                      <p className={`text-2xl font-bold font-mono ${scoreLevel.textColor}`}>{oportunidad.score}</p>
                      <p className="text-xs text-slate-500">Total</p>
                    </div>
                  </div>
                </div>

                {/* Keywords detectados */}
                {oportunidad.keywords_detectados && oportunidad.keywords_detectados.length > 0 && (
                  <div className="space-y-2">
                    <Label className="text-slate-400 text-xs">Keywords detectados en título</Label>
                    <div className="flex flex-wrap gap-1">
                      {oportunidad.keywords_detectados.map((kw, idx) => (
                        <Badge key={idx} variant="outline" className="border-purple-500/30 text-purple-400 text-xs">
                          {kw}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* ── Documentos del Expediente ── */}
                {docsLoaded && (
                  <div className="p-4 bg-slate-800/50 rounded-lg space-y-3">
                    <h4 className="text-slate-400 text-xs font-medium uppercase flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      Documentos del Expediente
                    </h4>

                    {/* PPT destacado si existe */}
                    {(pliego?.pliego_tecnico_url || docsData?.pliego_tecnico_url) && (
                      <div className="p-3 bg-gradient-to-r from-green-500/10 to-cyan-500/10 border border-green-500/40 rounded-lg">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <FileDown className="w-5 h-5 text-green-400" />
                            <div>
                              <span className="text-white font-medium text-sm">Pliego de Prescripciones Tecnicas</span>
                              <Badge className="ml-2 bg-green-500/20 text-green-400 text-[10px]">PPT</Badge>
                            </div>
                          </div>
                          <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white"
                            onClick={() => window.open(pliego?.pliego_tecnico_url || docsData?.pliego_tecnico_url, "_blank")}
                          >
                            <ExternalLink className="w-4 h-4 mr-1" /> Abrir PPT
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* Lista de documentos agrupados por tipo con botones de formato */}
                    {pliego?.disponible && pliego?.documentos && pliego.documentos.length > 0 ? (
                      <div className="space-y-2">
                        {(() => {
                          // Agrupar documentos por tipo
                          const tipoOrder = ["pliego", "adjudicacion", "anuncio", "formalizacion", "modificacion", "pliego_tecnico", "pliego_administrativo", "acta", "informe", "anexo", "otro"];
                          const grouped = {};
                          pliego.documentos.forEach(doc => {
                            const tipo = doc.tipo || "otro";
                            if (!grouped[tipo]) grouped[tipo] = [];
                            grouped[tipo].push(doc);
                          });

                          const tipoLabels = {
                            pliego_tecnico: "Pliego Técnico",
                            pliego_administrativo: "Pliego Administrativo",
                            pliego: "Pliego",
                            anuncio: "Anuncio de Licitación",
                            adjudicacion: "Adjudicación",
                            formalizacion: "Formalización",
                            modificacion: "Modificación",
                            acta: "Acta",
                            informe: "Informe",
                            anexo: "Anexo",
                            otro: "Documento",
                          };

                          const tipoBadgeColors = {
                            pliego_tecnico: "bg-green-500/20 text-green-400 border-green-500/30",
                            pliego_administrativo: "bg-blue-500/20 text-blue-400 border-blue-500/30",
                            pliego: "bg-cyan-500/20 text-cyan-400 border-cyan-500/30",
                            anuncio: "bg-indigo-500/20 text-indigo-400 border-indigo-500/30",
                            adjudicacion: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
                            formalizacion: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
                            modificacion: "bg-orange-500/20 text-orange-400 border-orange-500/30",
                            acta: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
                            informe: "bg-purple-500/20 text-purple-400 border-purple-500/30",
                            anexo: "bg-slate-500/20 text-slate-400 border-slate-500/30",
                          };

                          const formatColors = {
                            HTML: "text-orange-400 hover:bg-orange-500/15 border-orange-500/30",
                            XML: "text-violet-400 hover:bg-violet-500/15 border-violet-500/30",
                            PDF: "text-red-400 hover:bg-red-500/15 border-red-500/30",
                            SELLO: "text-slate-400 hover:bg-slate-500/15 border-slate-500/30",
                          };

                          const sortedTipos = Object.keys(grouped).sort(
                            (a, b) => (tipoOrder.indexOf(a) === -1 ? 99 : tipoOrder.indexOf(a)) -
                                      (tipoOrder.indexOf(b) === -1 ? 99 : tipoOrder.indexOf(b))
                          );

                          return sortedTipos.map(tipo => {
                            const docs = grouped[tipo];
                            const label = tipoLabels[tipo] || tipo;
                            const badgeColor = tipoBadgeColors[tipo] || "bg-slate-500/20 text-slate-400 border-slate-500/30";
                            const isPPT = tipo === "pliego_tecnico";

                            // Obtener formatos disponibles para este tipo
                            const formats = docs.map(d => ({
                              formato: (d.formato || "PDF").toUpperCase(),
                              url: d.url,
                            })).filter(d => d.url);

                            // Si hay un solo doc sin formato multi, mostrar estilo simple
                            const hasMultiFormats = formats.length > 1 || (formats.length === 1 && formats[0].formato !== "PDF");

                            return (
                              <div key={tipo} className={`flex items-center justify-between p-2 rounded ${isPPT ? 'bg-green-500/5 border border-green-500/20' : 'bg-slate-900/50'}`}>
                                <div className="flex items-center gap-2">
                                  <FileDown className={`w-4 h-4 ${isPPT ? 'text-green-400' : 'text-cyan-400'}`} />
                                  <span className="text-white text-sm">{label}</span>
                                  <Badge variant="outline" className={`text-[10px] ${badgeColor}`}>{label}</Badge>
                                </div>
                                <div className="flex items-center gap-1">
                                  {hasMultiFormats ? (
                                    formats.map((f, fi) => (
                                      <Button key={fi} variant="outline" size="sm"
                                        className={`h-6 text-[10px] px-2 border ${formatColors[f.formato] || formatColors.PDF}`}
                                        onClick={() => window.open(f.url, "_blank")}
                                        title={`Abrir ${label} en ${f.formato}`}
                                      >
                                        {f.formato}
                                      </Button>
                                    ))
                                  ) : formats.length > 0 ? (
                                    <Button variant="ghost" size="sm" className="text-cyan-400 hover:text-cyan-300"
                                      onClick={() => window.open(formats[0].url, "_blank")}
                                    >
                                      <ExternalLink className="w-4 h-4 mr-1" /> Abrir
                                    </Button>
                                  ) : null}
                                </div>
                              </div>
                            );
                          });
                        })()}
                      </div>
                    ) : (
                      <p className="text-slate-500 text-sm">No se encontraron documentos descargables. Pulsa "Analizar Pliego" para buscar.</p>
                    )}
                  </div>
                )}

                {/* Link directo PLACSP */}
                {oportunidad.url_placsp && (
                  <div className="p-3 bg-slate-800/30 rounded-lg">
                    <a href={oportunidad.url_placsp} target="_blank" rel="noopener noreferrer"
                      className="text-cyan-400 hover:underline text-sm flex items-center gap-2"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Ver todos los documentos en PLACSP (pliego, acta, anuncios)
                    </a>
                  </div>
                )}

                {/* ── Pain Analysis (IA) ── */}
                {loadingPain ? (
                  <div className="p-4 bg-slate-800/30 border border-cyan-500/30 rounded-lg flex items-center gap-3">
                    <Loader2 className="w-5 h-5 animate-spin text-cyan-400" />
                    <span className="text-slate-400 text-sm">Analizando dolor de subcontratación...</span>
                  </div>
                ) : painResult ? (
                  <div className="p-4 bg-gradient-to-r from-orange-500/10 to-red-500/10 border border-orange-500/30 rounded-lg space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Target className="w-4 h-4 text-orange-400" />
                        <span className="text-xs font-medium uppercase text-orange-400">Pain Score</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`text-2xl font-bold ${painResult.pain_score >= 75 ? 'text-red-400' :
                            painResult.pain_score >= 55 ? 'text-orange-400' :
                              painResult.pain_score >= 35 ? 'text-yellow-400' : 'text-slate-400'
                          }`}>
                          {painResult.pain_score}
                        </span>
                        <Badge className={`${painResult.pain_level === 'CRITICO' ? 'bg-red-500/20 text-red-400' :
                            painResult.pain_level === 'ALTO' ? 'bg-orange-500/20 text-orange-400' :
                              painResult.pain_level === 'MEDIO' ? 'bg-yellow-500/20 text-yellow-400' :
                                'bg-slate-500/20 text-slate-400'
                          }`}>
                          {painResult.pain_level}
                        </Badge>
                        {painResult.llm_provider && (
                          <Badge variant="outline" className="border-slate-600 text-slate-500 text-[10px]">
                            {painResult.llm_provider}
                          </Badge>
                        )}
                      </div>
                    </div>
                    {/* Pain Breakdown - 5 dimensiones */}
                    <div className="grid grid-cols-5 gap-2 mt-2">
                      {[
                        { key: 'd1_escala', label: 'Escala', max: 25, color: 'cyan' },
                        { key: 'd2_complejidad', label: 'Complejidad', max: 25, color: 'purple' },
                        { key: 'd3_cobertura', label: 'Cobertura', max: 20, color: 'blue' },
                        { key: 'd4_urgencia', label: 'Urgencia', max: 15, color: 'yellow' },
                        { key: 'd5_perfil', label: 'Perfil', max: 15, color: 'orange' },
                      ].map(dim => {
                        const val = painResult.breakdown?.[dim.key] || 0;
                        const pct = Math.round((val / dim.max) * 100);
                        return (
                          <div key={dim.key} className="text-center">
                            <p className={`text-lg font-bold text-${dim.color}-400`}>{val}</p>
                            <div className="w-full bg-slate-700 rounded-full h-1 mt-1">
                              <div className={`bg-${dim.color}-500 h-1 rounded-full`} style={{ width: `${pct}%` }} />
                            </div>
                            <p className="text-[10px] text-slate-500 mt-1">{dim.label}</p>
                          </div>
                        );
                      })}
                    </div>
                    {/* Resumen LLM */}
                    {painResult.detalles?.llm?.resumen && (
                      <p className="text-slate-300 text-sm mt-2 pt-2 border-t border-slate-700">
                        {painResult.detalles.llm.resumen}
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="p-4 bg-slate-800/30 border border-dashed border-slate-700 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-slate-500">
                        <Target className="w-4 h-4" />
                        <span className="text-xs font-medium uppercase">Pain Score (IA)</span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-cyan-400 hover:text-cyan-300 text-xs"
                        onClick={runPainAnalysis}
                        disabled={loadingPain}
                      >
                        Analizar Pain
                      </Button>
                    </div>
                    <p className="text-slate-500 text-sm mt-1">
                      Pulsa "Analizar Pliego" o el botón para ejecutar el análisis de dolor.
                    </p>
                  </div>
                )}

                {/* ── Pliego Analysis (IA) ── */}
                {loadingPliego ? (
                  <div className="p-4 bg-slate-800/30 border border-purple-500/30 rounded-lg flex items-center gap-3">
                    <Loader2 className="w-5 h-5 animate-spin text-purple-400" />
                    <span className="text-slate-400 text-sm">Analizando pliego con IA...</span>
                  </div>
                ) : pliegoResult ? (
                  <div className="space-y-3">
                    {/* Resumen ejecutivo */}
                    {pliegoResult.resumen_ejecutivo && (
                      <div className="p-4 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                        <h4 className="text-purple-400 text-xs font-medium uppercase mb-2 flex items-center gap-2">
                          <Briefcase className="w-4 h-4" />
                          Resumen Ejecutivo
                        </h4>
                        <p className="text-slate-300 text-sm whitespace-pre-line">{pliegoResult.resumen_ejecutivo}</p>
                      </div>
                    )}

                    {/* Tesoros ocultos */}
                    {pliegoResult.tesoros_ocultos && pliegoResult.tesoros_ocultos.length > 0 && (
                      <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                        <h4 className="text-yellow-400 text-xs font-medium uppercase mb-2">Tesoros Ocultos</h4>
                        <div className="space-y-2">
                          {pliegoResult.tesoros_ocultos.map((t, idx) => (
                            <div key={idx} className="flex items-start gap-2">
                              <Badge className={`mt-0.5 text-[10px] ${t.relevancia === 'ALTA' ? 'bg-red-500/20 text-red-400' :
                                  t.relevancia === 'MEDIA' ? 'bg-yellow-500/20 text-yellow-400' :
                                    'bg-slate-500/20 text-slate-400'
                                }`}>{t.relevancia}</Badge>
                              <div>
                                <p className="text-white text-sm">{t.hallazgo}</p>
                                <p className="text-cyan-400 text-xs">{t.oportunidad_srs}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Requisitos */}
                    {pliegoResult.requisitos && pliegoResult.requisitos.length > 0 && (
                      <div className="p-4 bg-slate-800/50 rounded-lg">
                        <h4 className="text-slate-400 text-xs font-medium uppercase mb-2">Requisitos</h4>
                        <div className="space-y-1">
                          {pliegoResult.requisitos.map((r, idx) => (
                            <div key={idx} className="flex items-center gap-2 text-sm">
                              <Badge className={`text-[10px] ${r.tipo === 'HARD' ? 'bg-red-500/20 text-red-400' : 'bg-green-500/20 text-green-400'
                                }`}>{r.tipo}</Badge>
                              <span className="text-slate-300">{r.descripcion}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Contacto extraído por IA */}
                    {pliegoResult.contacto_entidad && (
                      pliegoResult.contacto_entidad.email || pliegoResult.contacto_entidad.telefono ||
                      pliegoResult.contacto_entidad.direccion || pliegoResult.contacto_entidad.persona_contacto
                    ) && (
                        <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                          <h4 className="text-blue-400 text-xs font-medium uppercase mb-2 flex items-center gap-2">
                            <Mail className="w-4 h-4" />
                            Contacto (extraído del pliego por IA)
                          </h4>
                          <div className="grid grid-cols-2 gap-2">
                            {pliegoResult.contacto_entidad.persona_contacto && (
                              <div className="flex items-center gap-2">
                                <User className="w-4 h-4 text-slate-400" />
                                <span className="text-white text-sm">{pliegoResult.contacto_entidad.persona_contacto}</span>
                              </div>
                            )}
                            {pliegoResult.contacto_entidad.email && (
                              <div className="flex items-center gap-2">
                                <Mail className="w-4 h-4 text-slate-400" />
                                <a href={`mailto:${pliegoResult.contacto_entidad.email}`} className="text-cyan-400 hover:underline text-sm">
                                  {pliegoResult.contacto_entidad.email}
                                </a>
                              </div>
                            )}
                            {pliegoResult.contacto_entidad.telefono && (
                              <div className="flex items-center gap-2">
                                <Phone className="w-4 h-4 text-slate-400" />
                                <a href={`tel:${pliegoResult.contacto_entidad.telefono}`} className="text-cyan-400 hover:underline text-sm">
                                  {pliegoResult.contacto_entidad.telefono}
                                </a>
                              </div>
                            )}
                            {pliegoResult.contacto_entidad.direccion && (
                              <div className="col-span-2 flex items-center gap-2">
                                <MapPin className="w-4 h-4 text-slate-400" />
                                <span className="text-slate-300 text-sm">{pliegoResult.contacto_entidad.direccion}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                    {/* Certificaciones + Competidores en grid */}
                    <div className="grid grid-cols-2 gap-3">
                      {pliegoResult.certificaciones && pliegoResult.certificaciones.length > 0 && (
                        <div className="p-3 bg-slate-800/50 rounded-lg">
                          <h4 className="text-slate-400 text-xs font-medium uppercase mb-2">Certificaciones</h4>
                          <div className="flex flex-wrap gap-1">
                            {pliegoResult.certificaciones.map((c, idx) => (
                              <Badge key={idx} variant="outline" className="border-cyan-500/30 text-cyan-400 text-xs">{c}</Badge>
                            ))}
                          </div>
                        </div>
                      )}
                      {pliegoResult.competidores_probables && pliegoResult.competidores_probables.length > 0 && (
                        <div className="p-3 bg-slate-800/50 rounded-lg">
                          <h4 className="text-slate-400 text-xs font-medium uppercase mb-2">Competidores</h4>
                          <div className="space-y-1">
                            {pliegoResult.competidores_probables.map((c, idx) => (
                              <p key={idx} className="text-sm text-slate-300">{c.nombre}</p>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ) : null}
              </TabsContent>

              {/* ===================== TAB: GESTIÓN ===================== */}
              <TabsContent value="gestion" className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  {/* Estado */}
                  <div className="space-y-2">
                    <Label className="text-slate-400 text-xs">Estado de revisión</Label>
                    <Select
                      value={estado}
                      onValueChange={(v) => handleFieldChange("estado", v)}
                      disabled={oportunidad.estado === "convertida"}
                    >
                      <SelectTrigger className="theme-bg-tertiary theme-border theme-text">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="theme-bg-secondary theme-border">
                        <SelectItem value="nueva">Nuevo</SelectItem>
                        <SelectItem value="revisada">En revisión</SelectItem>
                        <SelectItem value="contactada">Contactada</SelectItem>
                        <SelectItem value="convertida" disabled>Convertida</SelectItem>
                        <SelectItem value="descartada">Descartada</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Asignado */}
                  <div className="space-y-2">
                    <Label className="text-slate-400 text-xs">Asignado a</Label>
                    <Select
                      value={asignadoId || "none"}
                      onValueChange={(v) => handleFieldChange("asignadoId", v === "none" ? "" : v)}
                    >
                      <SelectTrigger className="theme-bg-tertiary theme-border theme-text">
                        <SelectValue placeholder="Sin asignar" />
                      </SelectTrigger>
                      <SelectContent className="theme-bg-secondary theme-border">
                        <SelectItem value="none">Sin asignar</SelectItem>
                        {users.map((u) => (
                          <SelectItem key={u.user_id} value={u.user_id}>
                            {u.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Lead status */}
                {oportunidad.lead_id ? (
                  <div className="p-3 bg-green-500/10 rounded-lg border border-green-500/30">
                    <p className="text-green-400 text-sm flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4" />
                      Convertida a Lead
                    </p>
                  </div>
                ) : null}

                {/* Notas */}
                <div className="space-y-2">
                  <Label className="text-slate-400 text-xs">Notas</Label>
                  <Textarea
                    value={notas}
                    onChange={(e) => handleFieldChange("notas", e.target.value)}
                    placeholder="Añade notas sobre esta oportunidad..."
                    className="bg-slate-950 border-slate-700 min-h-[80px]"
                  />
                </div>

                {/* Historial */}
                <div className="space-y-3">
                  <Label className="text-slate-400 text-xs flex items-center gap-1">
                    <History className="w-3 h-3" /> Historial
                  </Label>

                  {/* Add activity */}
                  <div className="flex gap-2">
                    <Select value={activityType} onValueChange={setActivityType}>
                      <SelectTrigger className="w-[100px] theme-bg-tertiary theme-border theme-text">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="theme-bg-secondary theme-border">
                        <SelectItem value="nota">Nota</SelectItem>
                        <SelectItem value="llamada">Llamada</SelectItem>
                        <SelectItem value="email">Email</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input
                      value={newActivity}
                      onChange={(e) => setNewActivity(e.target.value)}
                      placeholder="Añadir actividad..."
                      className="flex-1 bg-slate-950 border-slate-700"
                      onKeyDown={(e) => e.key === "Enter" && handleAddActivity()}
                    />
                    <Button
                      onClick={handleAddActivity}
                      size="icon"
                      className="bg-cyan-600 hover:bg-cyan-700"
                      disabled={!newActivity.trim()}
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>

                  {/* Activity list */}
                  <div className="max-h-[120px] overflow-y-auto space-y-2">
                    {activities.length === 0 ? (
                      <p className="text-slate-500 text-sm text-center py-3">
                        Sin actividad registrada
                      </p>
                    ) : (
                      activities.map((act) => (
                        <div
                          key={act.activity_id}
                          className="p-2 bg-slate-800/50 rounded text-sm"
                        >
                          <div className="flex items-center justify-between text-xs text-slate-500">
                            <span>{act.tipo} • {act.user_name}</span>
                            <span>{formatDateTime(act.created_at)}</span>
                          </div>
                          <p className="text-slate-300 mt-1">{act.descripcion}</p>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </div>

          {/* Footer Actions */}
          <div className="pt-4 border-t theme-border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-2">
              <div className="flex items-center gap-2">
                <span className="text-xs theme-text-muted uppercase font-medium mr-2">Estado:</span>
                <Select
                  value={estado}
                  onValueChange={(v) => handleFieldChange("estado", v)}
                  disabled={oportunidad.estado === "convertida"}
                >
                  <SelectTrigger className="w-[140px] theme-bg-tertiary theme-border theme-text h-8 text-xs hover:border-teal-400/40 transition-colors">
                    <SelectValue placeholder="Selecciona..." />
                  </SelectTrigger>
                  <SelectContent className="theme-bg-secondary theme-border">
                    <SelectItem value="nueva">Nuevo</SelectItem>
                    <SelectItem value="revisada">En revisión</SelectItem>
                    <SelectItem value="contactada">Contactada</SelectItem>
                    <SelectItem value="convertida" disabled>Convertida</SelectItem>
                    <SelectItem value="descartada">Descartada</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {oportunidad.estado !== "convertida" && oportunidad.estado !== "descartada" && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDescartar}
                  className="border-red-500/40 text-red-400 hover:bg-red-500/10 hover:text-red-300 h-8"
                >
                  <XCircle className="w-4 h-4 mr-1.5" />
                  Descartar
                </Button>
              )}
            </div>

            <div className="flex items-center gap-2">
              {oportunidad.url_placsp && (
                <Button
                  variant="outline"
                  onClick={() => window.open(oportunidad.url_placsp, "_blank")}
                  className="border-slate-600 text-slate-300"
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Ver en PLACSP
                </Button>
              )}

              {hasChanges && (
                <Button
                  variant="outline"
                  onClick={handleSave}
                  disabled={loading}
                  className="border-cyan-500/50 text-cyan-400"
                >
                  Guardar
                </Button>
              )}

              {oportunidad.estado !== "convertida" && (
                <Button
                  onClick={() => setConvertirModalOpen(true)}
                  className="bg-cyan-600 hover:bg-cyan-700"
                >
                  <ArrowRight className="w-4 h-4 mr-2" />
                  Convertir a Lead
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Convertir a Lead Modal */}
      <ConvertirLeadModal
        open={convertirModalOpen}
        onClose={() => setConvertirModalOpen(false)}
        oportunidad={oportunidad}
        adjudicatarioData={adjData}
        onSuccess={() => {
          setConvertirModalOpen(false);
          onClose();
        }}
      />

      {/* Descartar con Feedback Modal */}
      <DescartarModal
        open={descartarModalOpen}
        onClose={() => setDescartarModalOpen(false)}
        oportunidad={oportunidad}
        onSuccess={() => {
          setDescartarModalOpen(false);
          onClose();
        }}
      />

      {/* v4.4.10 — Histórico por NIF del adjudicatario */}
      <AdjudicatarioHistorialModal
        open={historialAdjOpen}
        onClose={() => setHistorialAdjOpen(false)}
        adjudicatario={oportunidad.adjudicatario}
        nif={oportunidad.nif_adjudicatario}
      />
    </>
  );
}


// =====================================================================
// PostMortemBlock — v4.4.11 "¿Por qué ganó X y no Y?"
// =====================================================================

function PostMortemBlock({ oportunidad, acta, postmortem, onUpdated }) {
  const [loading, setLoading] = useState(false);
  const [boundaryError, setBoundaryError] = useState(null);

  // v4.4.21: acta_data puede venir como string Python o como objeto.
  // Para `hasActa` solo nos interesa SI existe — el backend parseará si hace falta.
  // v4.4.32: considerar también el state local `acta` (cargado desde
  // /spotter/documents/*) para habilitar el botón cuando los docs se acaban
  // de descargar y `oportunidad.acta_data` aún no se ha refrescado del prop.
  const actaRaw = oportunidad?.acta_data;
  let hasActa = false;
  try {
    if (acta && typeof acta === "object" && !Array.isArray(acta) && Object.keys(acta).length > 0) {
      hasActa = true;
    } else if (actaRaw && typeof actaRaw === "object" && !Array.isArray(actaRaw)) {
      hasActa = Object.keys(actaRaw).length > 0;
    } else if (typeof actaRaw === "string" && actaRaw.trim().length > 10) {
      hasActa = true;
    }
  } catch (e) {
    hasActa = false;
  }

  const hasPostmortem = !!(postmortem && postmortem.factor_ganador_principal);

  if (boundaryError) {
    return (
      <div className="p-4 bg-red-500/10 border border-red-400/30 rounded-lg text-sm text-red-300">
        <div className="font-semibold mb-1">Post-Mortem no disponible</div>
        <div className="text-xs opacity-80">{boundaryError}</div>
      </div>
    );
  }

  const runPostMortem = async () => {
    setLoading(true);
    try {
      const resp = await api.post(`/analyzer/postmortem/${oportunidad.oportunidad_id || oportunidad._id}`);
      if (resp.data?.analysis) {
        onUpdated?.({
          ...resp.data.analysis,
          provider: resp.data.provider,
          tokens_used: resp.data.tokens_used,
          analyzed_at: new Date().toISOString(),
        });
        toast.success("Análisis post-mortem generado");
      } else if (resp.data?.error) {
        toast.error(`IA falló: ${resp.data.error}`);
      }
    } catch (e) {
      const msg = e.response?.data?.detail || e.message;
      toast.error(`Error: ${msg}`);
    } finally {
      setLoading(false);
    }
  };

  // v4.4.32 contraste light/dark: texto más oscuro en light, más claro en dark
  const COLORS = {
    precio: "text-blue-700 dark:text-blue-300 bg-blue-500/15 border-blue-400/30",
    tecnico: "text-purple-700 dark:text-purple-300 bg-purple-500/15 border-purple-400/30",
    plazo: "text-orange-700 dark:text-orange-300 bg-orange-500/15 border-orange-400/30",
    experiencia: "text-amber-700 dark:text-amber-300 bg-amber-500/15 border-amber-400/30",
    ute_estrategica: "text-pink-700 dark:text-pink-300 bg-pink-500/15 border-pink-400/30",
    unico_ofertante: "text-slate-700 dark:text-slate-300 bg-slate-500/15 border-slate-400/30",
    otro: "text-slate-700 dark:text-slate-300 bg-slate-500/15 border-slate-400/30",
  };
  const factorClass = COLORS[postmortem?.factor_ganador_principal] || COLORS.otro;

  const BRECHA_LABEL = {
    amplia: "Brecha amplia con 2º",
    ajustada: "Brecha ajustada con 2º",
    sin_datos: "Sin datos del 2º",
  };

  return (
    <div className="p-4 bg-gradient-to-r from-teal-500/10 to-amber-500/10 border border-teal-400/40 rounded-lg space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <h4 className="theme-text font-semibold flex items-center gap-2">
            <Zap className="w-5 h-5 text-teal-600 dark:text-teal-400" />
            Post-Mortem IA — ¿Por qué ganó?
          </h4>
          <p className="theme-text-muted text-xs mt-1">
            Análisis competitivo: qué marcó la diferencia, brecha con el 2º, lecciones accionables y oportunidad de subcontratación.
          </p>
        </div>
        <Button
          className="bg-teal-600 hover:bg-teal-700 text-white shrink-0"
          size="sm"
          onClick={runPostMortem}
          disabled={loading || !hasActa}
          title={!hasActa ? "Necesita acta parseada (ejecuta 'Analizar Pliego' primero)" : "Analizar"}
        >
          {loading ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <TrendingUp className="w-4 h-4 mr-2" />
          )}
          {hasPostmortem ? "Regenerar" : "Analizar"}
        </Button>
      </div>

      {!hasActa && (
        <div className="text-xs theme-text-muted italic">
          Sin datos de acta parseada. Pulsa "Actualizar" en Análisis de Documentos para descargar el acta.
        </div>
      )}

      {hasPostmortem && (
        <div className="space-y-3 pt-2 border-t border-teal-400/20">
          {/* Factor ganador + brecha */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className={`p-3 rounded-lg border ${factorClass}`}>
              <div className="text-[10px] uppercase font-semibold opacity-80">Factor ganador</div>
              <div className="text-sm font-bold mt-0.5 capitalize">
                {(postmortem.factor_ganador_principal || "?").replace(/_/g, " ")}
              </div>
              <div className="text-xs mt-1 opacity-90">
                {postmortem.factor_ganador_detalle}
              </div>
            </div>
            <div className="p-3 rounded-lg theme-bg-tertiary theme-border border">
              <div className="text-[10px] uppercase font-semibold theme-text-muted">
                {BRECHA_LABEL[postmortem.brecha_con_segundo] || "Brecha"}
              </div>
              <div className="text-sm font-bold mt-0.5 theme-text">
                {postmortem.brecha_detalle}
              </div>
              <div className="text-xs mt-1 theme-text-muted">
                Competencia:{" "}
                <span className="capitalize theme-text">
                  {(postmortem.calidad_competencia || "?").replace("_", " ")}
                </span>
              </div>
            </div>
          </div>

          {/* Patrón órgano */}
          {postmortem.patron_organo && (
            <div className="p-3 rounded-lg theme-bg-tertiary theme-border border">
              <div className="text-[10px] uppercase font-semibold theme-text-muted mb-1">
                Patrón del órgano contratante
              </div>
              <p className="text-sm theme-text">{postmortem.patron_organo}</p>
            </div>
          )}

          {/* Oportunidad SRS */}
          {postmortem.oportunidad_srs_subcontratacion && (
            <div className="p-3 rounded-lg bg-teal-500/10 border border-teal-500/40">
              <div className="text-[10px] uppercase font-semibold text-teal-700 dark:text-teal-300 mb-1">
                🎯 Oportunidad SRS de subcontratación
              </div>
              <p className="text-sm text-slate-800 dark:text-slate-100">
                {postmortem.oportunidad_srs_subcontratacion}
              </p>
            </div>
          )}

          {/* Lecciones */}
          {postmortem.lecciones_accionables && postmortem.lecciones_accionables.length > 0 && (
            <div>
              <div className="text-[10px] uppercase font-semibold theme-text-muted mb-1.5">
                Lecciones para futuras ofertas
              </div>
              <ul className="space-y-1">
                {postmortem.lecciones_accionables.map((l, i) => (
                  <li key={i} className="text-sm theme-text flex gap-2">
                    <span className="text-teal-600 dark:text-teal-400 shrink-0">▸</span>
                    <span>{l}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Alertas */}
          {postmortem.alertas && postmortem.alertas.length > 0 && (
            <div className="p-3 rounded-lg bg-amber-500/15 border border-amber-500/50">
              <div className="text-[10px] uppercase font-semibold text-amber-700 dark:text-amber-300 mb-1">
                ⚠️ Alertas
              </div>
              <ul className="space-y-0.5">
                {postmortem.alertas.map((a, i) => (
                  <li key={i} className="text-xs text-amber-900 dark:text-amber-100">• {a}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Metadata */}
          <div className="flex items-start gap-3 text-[10px] theme-text-muted pt-1">
            <span>Confianza: <strong className="capitalize theme-text">{postmortem.confianza_analisis || "?"}</strong></span>
            {postmortem.razon_confianza && (
              <span className="italic flex-1">— {postmortem.razon_confianza}</span>
            )}
            {postmortem.provider && (
              <span className="ml-auto whitespace-nowrap">{postmortem.provider} · {postmortem.tokens_used} tokens</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
